import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package,
  Clock,
  TrendingUp,
  Users,
  DollarSign,
  ShoppingCart,
  AlertCircle,
  RefreshCw,
  Calendar,
  MapPin,
  FileText,
  CheckCircle,
  Activity,
  ArrowRight,
  MessageSquare
} from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { showError } from '../../utils/notificationHandler';
import PageHeader from '../shared/PageHeader';
import Button from '../shared/Button';
import ReactECharts from 'echarts-for-react';
import StatCard from '../shared/StatCard';
import { realtimeService } from '../../services/realtimeService';
import { startOfMonth, subMonths, format } from 'date-fns';
import { useCompanyData } from '../../hooks/useCompanyData';

const CompanyAdminDashboard = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const { fetchCompanyData } = useCompanyData();
  const [trendPeriod, setTrendPeriod] = useState('12months'); // 'week', 'month', '12months'
  const [metrics, setMetrics] = useState({
    totalOrders: 0,
    ordersThisMonth: 0,
    pendingOrders: 0,
    activeBackorders: 0,
    totalUsers: 0,
    activeUsers: 0,
    spendThisMonth: 0,
    totalAddresses: 0
  });

  // Format date to DD/MM/YYYY (Australian format)
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };
  const [monthlyChanges, setMonthlyChanges] = useState({
    ordersChange: 0,
    spendChange: 0
  });
  const [ordersByStatus, setOrdersByStatus] = useState([]);
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [activeBackorders, setActiveBackorders] = useState([]);
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [realtimeConnected, setRealtimeConnected] = useState(false);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchDashboardData();

      // Subscribe to realtime order updates
      const unsubscribe = realtimeService.subscribeToOrders(
        'company-admin-dashboard',
        handleOrderUpdate,
        {
          events: ['INSERT', 'UPDATE', 'DELETE'],
          showNotifications: false
        }
      );

      // Monitor realtime connection status
      const statusInterval = setInterval(() => {
        setRealtimeConnected(realtimeService.isConnected());
      }, 1000);

      return () => {
        unsubscribe();
        clearInterval(statusInterval);
      };
    }
  }, [currentUser]);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('accounts')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (error) throw error;
        setCurrentUser(data);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      showError(error, 'Failed to fetch user details');
    }
  };

  const handleOrderUpdate = ({ eventType, newRecord, oldRecord }) => {
    console.log('[Company Admin Dashboard] Order update received:', eventType);
    
    // Only refresh if the order belongs to this company
    if (currentUser && newRecord) {
      const checkOrder = async () => {
        const { data } = await supabase
          .from('accounts')
          .select('deb_id')
          .eq('id', newRecord.user_id)
          .single();
        
        if (data && data.deb_id === currentUser.deb_id) {
          fetchDashboardData();
        }
      };
      checkOrder();
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Use Edge Function to fetch all data
      await Promise.all([
        fetchMetricsViaEdgeFunction(),
        fetchChartsViaEdgeFunction(),
        fetchRecentDataViaEdgeFunction(),
        fetchInquiriesViaEdgeFunction()
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError(error, 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMetricsViaEdgeFunction = async () => {
    try {
      const data = await fetchCompanyData('metrics');
      setMetrics(data.metrics);
      setMonthlyChanges(data.monthlyChanges);
    } catch (error) {
      console.error('Error fetching metrics via edge function:', error);
      throw error;
    }
  };

  const fetchChartsViaEdgeFunction = async () => {
    try {
      const data = await fetchCompanyData('charts');
      setOrdersByStatus(data.ordersByStatus);
      setMonthlyTrends(data.monthlyTrends);
    } catch (error) {
      console.error('Error fetching charts via edge function:', error);
      throw error;
    }
  };

  const fetchRecentDataViaEdgeFunction = async () => {
    try {
      const data = await fetchCompanyData('recent');
      setRecentOrders(data.recentOrders);
      setTopUsers(data.topUsers);
      setActiveBackorders(data.activeBackorders);
    } catch (error) {
      console.error('Error fetching recent data via edge function:', error);
      throw error;
    }
  };

  const fetchInquiriesViaEdgeFunction = async () => {
    try {
      const data = await fetchCompanyData('inquiries');
      setRecentInquiries(data.inquiries);
    } catch (error) {
      console.error('Error fetching inquiries via edge function:', error);
      // Don't throw - inquiries are optional
    }
  };

  // Legacy fetch functions removed - now using Edge Function
  // These are kept as comments for reference if rollback is needed

  const getOrderStatusChartOption = () => {
    if (ordersByStatus.length === 0) return {};

    const statusColors = {
      'Complete': '#10b981',
      'Pending': '#f59e0b',
      'Processing': '#3b82f6',
      'Cancelled': '#ef4444',
      'pending_approval': '#f97316'
    };

    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} orders ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        textStyle: { color: '#374151' }
      },
      series: [
        {
          name: 'Orders',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: false,
            position: 'center'
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 20,
              fontWeight: 'bold'
            }
          },
          labelLine: {
            show: false
          },
          data: ordersByStatus.map(item => ({
            value: item.count,
            name: item.status,
            itemStyle: {
              color: statusColors[item.status] || '#6b7280'
            }
          }))
        }
      ]
    };
  };

  const getFilteredTrendData = () => {
    if (monthlyTrends.length === 0) return [];
    
    switch (trendPeriod) {
      case 'week':
        return monthlyTrends.slice(-1); // Last week (last data point)
      case 'month':
        return monthlyTrends.slice(-1); // Last month (last data point)
      case '12months':
      default:
        return monthlyTrends; // All 12 months
    }
  };

  const getMonthlyTrendsChartOption = () => {
    const filteredData = getFilteredTrendData();
    if (filteredData.length === 0) return {};

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        }
      },
      legend: {
        data: ['Order Count', 'Order Value'],
        textStyle: { color: '#374151' }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: filteredData.map(t => t.month),
        axisLabel: { color: '#374151' }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Orders',
          position: 'left',
          axisLabel: { color: '#374151' }
        },
        {
          type: 'value',
          name: 'Value ($)',
          position: 'right',
          axisLabel: {
            color: '#374151',
            formatter: '${value}'
          }
        }
      ],
      series: [
        {
          name: 'Order Count',
          type: 'line',
          smooth: true,
          data: filteredData.map(t => t.count),
          itemStyle: { color: '#3b82f6' },
          areaStyle: { opacity: 0.3 }
        },
        {
          name: 'Order Value',
          type: 'line',
          smooth: true,
          yAxisIndex: 1,
          data: filteredData.map(t => t.value),
          itemStyle: { color: '#10b981' },
          areaStyle: { opacity: 0.3 }
        }
      ]
    };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Complete':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Pending':
      case 'pending_approval':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Processing':
        return <Activity className="h-4 w-4 text-blue-500" />;
      case 'Cancelled':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Package className="h-4 w-4 text-gray-500" />;
    }
  };

  const companyName = currentUser?.company_name || 'Your Company';

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Company Dashboard"
        description={`Overview for ${companyName}`}
        meta={realtimeConnected && (
          <div className="flex items-center px-3 py-1 bg-green-50 border border-green-200 rounded-full">
            <div className="h-2 w-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            <span className="text-xs font-medium text-green-700">Live</span>
          </div>
        )}
        actions={
          <Button
            variant="secondary"
            onClick={fetchDashboardData}
            loading={loading}
            icon={<RefreshCw className="h-4 w-4" />}
          >
            Refresh
          </Button>
        }
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Orders"
          value={metrics.totalOrders}
          icon={Package}
          color="text-gray-500"
          loading={loading}
        />
        <StatCard
          title="This Month"
          value={metrics.ordersThisMonth}
          icon={Calendar}
          color="text-gray-500"
          loading={loading}
        />
        <StatCard
          title="Pending Orders"
          value={metrics.pendingOrders}
          icon={Clock}
          color="text-yellow-600"
          loading={loading}
        />
        <StatCard
          title="Active Backorders"
          value={metrics.activeBackorders}
          icon={AlertCircle}
          color="text-red-600"
          loading={loading}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Monthly Spend"
          value={`$${metrics.spendThisMonth.toFixed(2)}`}
          icon={DollarSign}
          color="text-gray-500"
          loading={loading}
        />
        <StatCard
          title="Company Users"
          value={metrics.totalUsers}
          icon={Users}
          color="text-gray-500"
          loading={loading}
        />
        <StatCard
          title="Active Users"
          value={metrics.activeUsers}
          icon={Activity}
          color="text-gray-500"
          loading={loading}
        />
        <StatCard
          title="Delivery Addresses"
          value={metrics.totalAddresses}
          icon={MapPin}
          color="text-gray-500"
          loading={loading}
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Order Status Distribution */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
        >
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              Order Status Distribution
            </h3>
            <div className="mt-4">
              {loading ? (
                <div className="animate-pulse h-64 bg-gray-200 rounded"></div>
              ) : ordersByStatus.length > 0 ? (
                <ReactECharts 
                  option={getOrderStatusChartOption()} 
                  style={{ height: '300px' }}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <div className="text-center">
                    <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No order data available</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Monthly Trends */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
        >
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                Order Trends
              </h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => setTrendPeriod('week')}
                  className={`px-3 py-1 text-xs font-medium rounded-md ${
                    trendPeriod === 'week'
                      ? 'bg-brand text-black'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setTrendPeriod('month')}
                  className={`px-3 py-1 text-xs font-medium rounded-md ${
                    trendPeriod === 'month'
                      ? 'bg-brand text-black'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Month
                </button>
                <button
                  onClick={() => setTrendPeriod('12months')}
                  className={`px-3 py-1 text-xs font-medium rounded-md ${
                    trendPeriod === '12months'
                      ? 'bg-brand text-black'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  12 Months
                </button>
              </div>
            </div>
            <div className="mt-4">
              {loading ? (
                <div className="animate-pulse h-64 bg-gray-200 rounded"></div>
              ) : monthlyTrends.length > 0 ? (
                <ReactECharts 
                  option={getMonthlyTrendsChartOption()} 
                  style={{ height: '300px' }}
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500">
                  <div className="text-center">
                    <TrendingUp className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No trend data available</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
        >
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                Recent Orders
              </h3>
              <button
                onClick={() => history.push('/admin/company-orders')}
                className="text-sm text-brand hover:text-brand-hover font-medium flex items-center"
              >
                View All <ArrowRight className="h-3 w-3 ml-1" />
              </button>
            </div>
            <div className="mt-4">
              {loading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="animate-pulse h-16 bg-gray-200 rounded"></div>
                  ))}
                </div>
              ) : recentOrders.length > 0 ? (
                <div className="space-y-3">
                  {recentOrders.map((order) => (
                    <div
                      key={order.id}
                      onClick={() => history.push('/admin/company-orders')}
                      className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-gray-50 hover:border-brand transition-all"
                    >
                      <div className="flex items-center space-x-3">
                        {getStatusIcon(order.status)}
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Order #{order.order_number}
                          </p>
                          <p className="text-xs text-gray-500">
                            {order.accounts?.full_name || order.accounts?.email}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          ${parseFloat(order.total_amount || 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No recent orders</p>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Top Users & Backorders */}
        <div className="space-y-6">
          {/* Top Ordering Users */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
          >
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">
                  Top Ordering Users
                </h3>
                <button
                  onClick={() => history.push('/admin/users')}
                  className="text-sm text-brand hover:text-brand-hover font-medium flex items-center"
                >
                  Manage <ArrowRight className="h-3 w-3 ml-1" />
                </button>
              </div>
              <div className="mt-4">
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="animate-pulse h-12 bg-gray-200 rounded"></div>
                    ))}
                  </div>
                ) : topUsers.length > 0 ? (
                  <div className="space-y-3">
                    {topUsers.map((user, index) => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-brand rounded-full flex items-center justify-center">
                            <span className="text-sm font-bold text-brand-dark">#{index + 1}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                            <p className="text-xs text-gray-500">{user.orderCount} orders</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            ${user.totalSpent.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No user activity yet</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Active Backorders Widget */}
          {activeBackorders.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
            >
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900">
                    Active Backorders
                  </h3>
                </div>
                <div className="mt-4 space-y-3">
                  {activeBackorders.map((backorder) => (
                    <div key={backorder.id} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {backorder.part_number}
                          </p>
                          {backorder.description && (
                            <p className="text-xs text-gray-600 mt-0.5">
                              {backorder.description}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Order #{backorder.order_number} • Qty: {backorder.quantity}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            backorder.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            backorder.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {backorder.status}
                          </span>
                          {backorder.expected_date && (
                            <p className="text-xs text-gray-500 mt-1">
                              ETA: {formatDate(backorder.expected_date)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200"
      >
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <button
              onClick={() => history.push('/admin/users')}
              className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand text-black"
            >
              <Users className="mr-2 h-5 w-5" />
              Manage Users
            </button>
            <button
              onClick={() => history.push('/admin/company-orders')}
              className="inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
            >
              <Package className="mr-2 h-5 w-5" />
              View Orders
            </button>
            <button
              onClick={() => history.push('/admin/delivery-addresses')}
              className="inline-flex items-center justify-center px-4 py-3 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
            >
              <MapPin className="mr-2 h-5 w-5" />
              Delivery Addresses
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CompanyAdminDashboard;